import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiTrash2, FiTrendingUp, FiTrendingDown, FiMinus } = FiIcons;

const BMIHistory = ({ history, onClear }) => {
  const getBMITrend = (current, previous) => {
    if (!previous) return null;
    const diff = current - previous;
    if (Math.abs(diff) < 0.1) return { icon: FiMinus, color: 'text-gray-500', text: 'No change' };
    if (diff > 0) return { icon: FiTrendingUp, color: 'text-red-500', text: `+${diff.toFixed(1)}` };
    return { icon: FiTrendingDown, color: 'text-green-500', text: diff.toFixed(1) };
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { category: 'Normal', color: 'text-green-600' };
    if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' };
    return { category: 'Obese', color: 'text-red-600' };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-xl p-8"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={FiClock} className="text-2xl text-blue-500 mr-3" />
          <h3 className="text-2xl font-bold text-gray-800">BMI History</h3>
        </div>
        <button
          onClick={onClear}
          className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiTrash2} className="mr-2" />
          Clear History
        </button>
      </div>

      <div className="space-y-3">
        {history.map((entry, index) => {
          const { category, color } = getBMICategory(entry.bmi);
          const trend = getBMITrend(entry.bmi, history[index + 1]?.bmi);

          return (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">{entry.bmi}</div>
                  <div className={`text-sm font-medium ${color}`}>{category}</div>
                </div>
                <div className="text-sm text-gray-600">
                  <div>{entry.weight} {entry.unit === 'metric' ? 'kg' : 'lbs'}</div>
                  <div>{entry.height} {entry.unit === 'metric' ? 'cm' : 'in'}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {trend && (
                  <div className={`flex items-center ${trend.color}`}>
                    <SafeIcon icon={trend.icon} className="mr-1" />
                    <span className="text-sm font-medium">{trend.text}</span>
                  </div>
                )}
                <div className="text-sm text-gray-500">{entry.date}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {history.length > 3 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Tracking your BMI over time helps monitor your weight loss progress. 
            Aim for gradual, sustainable changes for the best long-term results.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default BMIHistory;